import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Play, Lock, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useWedding } from '../../../context/WeddingContext';
import OkoiyaWrapped from './OkoiyaWrapped';

/**
 * MemoryReportCard
 * Renders nothing until the wedding date has passed.
 * Strictly premium: free couples see a locked teaser.
 * PDF/download removed — Wrapped only.
 */
export default function MemoryReportCard() {
  const { wedding, isPremium, refreshWedding } = useWedding();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [wrappedData, setWrappedData] = useState(null);

  if (!wedding?.wedding_date) return null;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const weddingDay = new Date(wedding.wedding_date + 'T00:00:00');
  if (weddingDay >= today) return null;

  const readyDate = new Date(weddingDay);
  readyDate.setDate(readyDate.getDate() + 7);
  const past7Days = today >= readyDate;
  const reportPath = wedding.memory_report_path;

  // ── actions ─────────────────────────────────────────────────────
  const handleWrapped = async () => {
    setBusy(true); setError('');
    try {
      const { data, error: e } = await supabase.rpc('get_memory_report_data', {
        p_wedding_id: wedding.id,
      });
      if (e || !data) throw e || new Error('no data');
      setWrappedData(data);
    } catch { setError('Could not load your Wrapped. Please try again.'); }
    finally { setBusy(false); }
  };

  const handleGenerate = async () => {
    setBusy(true); setError('');
    try {
      const { data, error: e } = await supabase.functions.invoke(
        'generate-memory-report',
        { body: { mode: 'manual', wedding_id: wedding.id } }
      );
      if (e || data?.error) throw e || new Error(data.error);
      if (typeof refreshWedding === 'function') await refreshWedding();
      else window.location.reload();
    } catch {
      setError('Could not prepare your Wrapped. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  // ── shared styles ───────────────────────────────────────────────
  const cardBase = {
    borderRadius: 16,
    padding: '28px 28px 24px',
    marginBottom: 24,
    background: 'linear-gradient(135deg, #C4826A 0%, #A5654E 100%)',
    color: '#fff',
    boxShadow: '0 8px 24px rgba(196,130,106,0.30)',
  };
  const titleStyle = {
    fontFamily: 'Gibs, serif', fontSize: 22, margin: 0,
    display: 'flex', alignItems: 'center', gap: 10,
  };
  const bodyStyle = {
    fontFamily: 'Nunito Sans, sans-serif', fontSize: 14,
    lineHeight: 1.65, opacity: 0.95, margin: '10px 0 18px', maxWidth: 460,
  };
  const btnWhite = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: '#fff', color: '#A5654E', border: 'none',
    borderRadius: 10, padding: '11px 18px', cursor: 'pointer',
    fontFamily: 'Nunito Sans, sans-serif', fontSize: 14, fontWeight: 700,
  };
  const spinner = <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />;

  // ── free plan: locked teaser ────────────────────────────────────
  if (!isPremium) {
    return (
      <div className="fade-up" style={{ ...cardBase, background: '#1A1A1A' }}>
        <h3 style={titleStyle}><Lock size={20} /> Your Wedding Wrapped</h3>
        <p style={bodyStyle}>
          Premium couples receive a beautiful animated recap of their wedding —
          every number, every memory, every moment you planned for.
        </p>
        <button style={btnWhite} onClick={() => navigate('/pricing')}>
          <Sparkles size={16} /> Upgrade to unlock
        </button>
      </div>
    );
  }

  // ── premium, data ready (report generated by cron or manual) ────
  if (reportPath) {
    return (
      <>
        <div className="fade-up" style={cardBase}>
          <h3 style={titleStyle}><Sparkles size={20} /> Your Wedding Wrapped is Ready</h3>
          <p style={bodyStyle}>
            Your wedding story — every number, every face, every moment —
            animated just for you.
          </p>
          <button style={btnWhite} onClick={handleWrapped} disabled={busy}>
            {busy ? spinner : <Play size={16} />} Watch Your Wrapped
          </button>
          {error && (
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: 12, marginTop: 12, color: '#FFE3D6' }}>
              {error}
            </p>
          )}
        </div>

        {wrappedData && (
          <OkoiyaWrapped data={wrappedData} onClose={() => setWrappedData(null)} />
        )}
      </>
    );
  }

  // ── premium, 7+ days passed, cron hasn't run yet ─────────────────
  if (past7Days) {
    return (
      <div className="fade-up" style={cardBase}>
        <h3 style={titleStyle}><Sparkles size={20} /> Your Wedding Wrapped</h3>
        <p style={bodyStyle}>
          Your wedding story is ready to be assembled — tap below and
          we'll put it together right now.
        </p>
        <button style={btnWhite} onClick={handleGenerate} disabled={busy}>
          {busy ? spinner : <Play size={16} />} Prepare my Wrapped
        </button>
        {error && (
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: 12, marginTop: 12, color: '#FFE3D6' }}>
            {error}
          </p>
        )}
      </div>
    );
  }

  // ── premium, within the first 7 days ────────────────────────────
  return (
    <div className="fade-up" style={cardBase}>
      <h3 style={titleStyle}><Sparkles size={20} /> Your Wedding Wrapped</h3>
      <p style={{ ...bodyStyle, marginBottom: 4 }}>
        Your Wrapped will be ready on{' '}
        {readyDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
        We'll email you the moment it is.
      </p>
    </div>
  );
}
